import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import "./ProjectCards.css";

const CSV_URL = process.env.PUBLIC_URL + "/test5.csv";

const ProjectsCard = () => {
  const [projects, setProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [techStack, setTechStack] = useState("");
  const [domain, setDomain] = useState("");
  const [organization, setOrganization] = useState("");
  const [focusedInput, setFocusedInput] = useState(null);
  const [visibleProjects, setVisibleProjects] = useState(15);
  const navigate = useNavigate();

  /* ---------- Load CSV ---------- */
  useEffect(() => {
    Papa.parse(CSV_URL, {
      download: true,
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const sorted = results.data.sort((a, b) =>
          (a["org title"] || "").localeCompare(b["org title"] || "")
        );
        setProjects(sorted);
      },
    });
  }, []);

  /* ---------- Global Search (ALL FIELDS) ---------- */
  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      const hay = [
        p["project title"],
        p["org title"],
        p["contributor name"],
        p["mentor name"],
        p["domain"],
        ...(p["All tech stack"]?.split(",").map((t) => t.trim()) ?? []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch = hay.includes(searchTerm.toLowerCase());

      const matchesTechStack =
        !techStack ||
        p["All tech stack"]?.toLowerCase().includes(techStack.toLowerCase());

      const matchesDomain = !domain || p["domain"] === domain;
      const matchesOrg = !organization || p["org title"] === organization;

      return (
        matchesSearch && matchesTechStack && matchesDomain && matchesOrg
      );
    });
  }, [projects, searchTerm, techStack, domain, organization]);

  const handleLoadMore = () => setVisibleProjects(filteredProjects.length);

  const formatTechStack = (techString) => {
    if (!techString) return "N/A";
    const arr = techString
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    return arr.length <= 5 ? arr.join(", ") : arr.slice(0, 5).join(", ") + ", ...";
  };

  /* ---------- ✅ FIXED Custom Select with Typing ---------- */
  const CustomSelect = ({ id, value, onChange, rawOptions, placeholder }) => {
    const [open, setOpen] = useState(false);
    const [innerSearch, setInnerSearch] = useState("");
    const wrapperRef = useRef(null);
    const searchInputRef = useRef(null);

    // Close dropdown if clicked outside
    useEffect(() => {
      const handler = (e) => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
          setOpen(false);
        }
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, []);

    const toggleOpen = () => {
      setOpen((prev) => {
        const willOpen = !prev;
        if (willOpen && searchInputRef.current) {
          setTimeout(() => searchInputRef.current.focus(), 0);
        }
        return willOpen;
      });
    };

    const filteredOptions = useMemo(() => {
      if (!innerSearch) return rawOptions;
      const term = innerSearch.toLowerCase();
      return rawOptions.filter((opt) => opt.toLowerCase().includes(term));
    }, [rawOptions, innerSearch]);

    // Clear search when closed
    useEffect(() => {
      if (!open) setInnerSearch("");
    }, [open]);

    return (
      <div
        className="pc-select-wrapper"
        ref={wrapperRef}
        tabIndex={0}
        onFocus={() => setFocusedInput(id)}
        onBlur={(e) => {
          // Only close if blur goes outside dropdown
          if (
            wrapperRef.current &&
            !wrapperRef.current.contains(e.relatedTarget)
          ) {
            setOpen(false);
            setFocusedInput(null);
          }
        }}
      >
        <div
          className={`pc-select-custom ${open ? "open" : ""} ${focusedInput === id ? "pc-select-focus" : ""
            }`}
          onClick={(e) => {
            e.stopPropagation();
            toggleOpen();
          }}
        >
          <span className="pc-select-label">{value || placeholder}</span>
          <span className="pc-select-arrow"></span>
        </div>

        {open && (
          <div
            className="pc-select-options"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              ref={searchInputRef}
              type="text"
              className="pc-select-search"
              placeholder="Type to filter..."
              value={innerSearch}
              onChange={(e) => setInnerSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            {filteredOptions.length ? (
              filteredOptions.map((opt) => (
                <div
                  key={opt}
                  className={`pc-select-option ${opt === value ? "selected" : ""}`}
                  onClick={() => {
                    onChange(opt);
                    setOpen(false);
                  }}
                >
                  {opt}
                </div>
              ))
            ) : (
              <div className="pc-select-option pc-select-no-result">
                No results
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  /* ---------- Extract Options ---------- */
  const techOptions = useMemo(() => {
    const set = new Set(
      projects.flatMap((p) =>
        p["All tech stack"]
          ? p["All tech stack"]
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
          : []
      )
    );
    return [...set].sort();
  }, [projects]);

  const domainOptions = useMemo(() => {
    const set = new Set(projects.map((p) => p["domain"]).filter(Boolean));
    return [...set];
  }, [projects]);

  const orgOptions = useMemo(() => {
    const set = new Set(projects.map((p) => p["org title"]).filter(Boolean));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [projects]);

  /* ---------- Render ---------- */
  return (
    <div className="pc-page">
      {/* HERO + BACK ARROW */}
      <section className="pc-hero">
        <div className="pc-hero-container">
          <div className="pc-hero-header">
            <button
              className="pc-back-arrow"
              onClick={() => navigate("/dmp2025")}
              aria-label="Go back"
            >
              ←
            </button>
            <h1 className="pc-hero-title">DMP 2025 Completed Projects</h1>
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <div className="pc-filters-wrap">
        <input
          type="text"
          placeholder="Search projects..."
          className={`pc-search-input${focusedInput === "search" ? " pc-search-input-focus" : ""
            }`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setFocusedInput("search")}
          onBlur={() => setFocusedInput(null)}
        />

        <CustomSelect
          id="techStack"
          value={techStack}
          onChange={setTechStack}
          rawOptions={techOptions}
          placeholder="All Tech Stack"
        />
        <CustomSelect
          id="domain"
          value={domain}
          onChange={setDomain}
          rawOptions={domainOptions}
          placeholder="All Domain"
        />
        <CustomSelect
          id="organization"
          value={organization}
          onChange={setOrganization}
          rawOptions={orgOptions}
          placeholder="All Organization"
        />
      </div>

      {/* PROJECT GRID */}
      <main className="pc-grid-wrap">
        <div className="pc-grid">
          {filteredProjects.slice(0, visibleProjects).map((p, idx) => (
            <article
              key={idx}
              className="pc-card"
              onClick={() =>
                navigate(`/project/${idx + 1}`, {
                  state: { project: p, index: idx },
                })
              }
            >
              <div className="pc-avatar-org-row">
                {p["org logo"] ? (
                  <img
                    src={p["org logo"]}
                    alt={`${p["org title"]} logo`}
                    className="pc-avatar"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <div
                  className="pc-avatar pc-avatar-fallback"
                  style={{ display: p["org logo"] ? "none" : "flex" }}
                >
                  {p["org title"]?.charAt(0)}
                </div>
                <div className="pc-org-name">{p["org title"]}</div>
              </div>

              <div className="pc-meta">
                <div className="pc-meta-columns">
                  <div className="pc-meta-column">
                    <span className="pc-meta-label">Contributor:</span>
                    <span className="pc-contributor-capsule">
                      {p["contributor name"] || "N/A"}
                    </span>
                  </div>
                  <div className="pc-meta-column">
                    <span className="pc-meta-label">Mentor:</span>
                    <span className="pc-mentor-capsule">
                      {p["mentor name"] || "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              <h4 className="pc-title">{p["project title"]}</h4>
              <div className="pc-languages">
                Tech Stack: {formatTechStack(p["All tech stack"])}
              </div>
            </article>
          ))}
        </div>
      </main>

      {visibleProjects < filteredProjects.length && (
        <div className="pc-load-more">
          <button
            className="pc-load-more-button"
            onClick={handleLoadMore}
          >
            Load More
          </button>
        </div>
      )}
    </div>
  );
};

export default ProjectsCard;
